import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';

// Browsers report .vtt as text/vtt, .srt as application/x-subrip (or text/srt) and often nothing at all.
const SCRIPT_MIME_TYPES = ['text/plain', 'text/vtt', 'application/x-subrip', 'text/srt'];


// Hundreds of identical diagnostics (one per script line) are noise. Group them by code with a count and
// a short list of lines; a warning that carries no code is its own group, shown as written.
export function groupWarnings(warnings) {
	const groups = new Map();
	for (const warning of warnings) {
		const key = warning.code || warning.message;
		if (!groups.has(key)) groups.set(key, { key, code: warning.code, count: 0, lines: [], message: warning.message });
		const group = groups.get(key);
		group.count += 1;
		if (warning.line) group.lines.push(warning.line);
	}
	return [...groups.values()];
}
const MAX_LINES_SHOWN = 40;
function validText(text, maxBytes) {
	return typeof text === 'string' && Boolean(text.trim()) && !/[\u0000\uFFFD]/.test(text) && new Blob([text]).size <= maxBytes;
}

function validPreview(value) {
	return (
		value &&
		typeof value.importId === 'string' &&
		Boolean(value.importId.trim()) &&
		typeof value.text === 'string' &&
		typeof value.valid === 'boolean' &&
		Array.isArray(value.warnings) &&
		value.warnings.every((w) => w && typeof w.id === 'string' && w.id && typeof w.message === 'string') &&
		new Set(value.warnings.map((w) => w.id)).size === value.warnings.length &&
		Array.isArray(value.exclusions) &&
		value.exclusions.every((e) => e && typeof e.text === 'string' && typeof e.reason === 'string')
	);
}

/**
 * Reusable import panel; deliberately NOT connected to any API or auth client.
 * Required props (adapter operations must be bound to the active tenant/channel):
 * - channelId: stable string identifying the channel; change it to discard local
 *   state and invalidate pending completions. Include tenant identity if needed.
 * - createImport({ filename, text }): Promise<{
 *     importId: nonempty string, text: string, valid: boolean,
 *     warnings: Array<{ id: unique nonempty string, message: string }>,
 *     exclusions: Array<{ text: string, reason: string, line?: number }>
 *   }>. Text is the SERVER's spoken preview, not a client-side parser result.
 *   Always include warnings/exclusions arrays, even when empty. An adapter must
 *   preserve every warning/exclusion; never silently discard unknown warnings.
 * - commitImport({ importId, text, acknowledgedWarnings: string[] }):
 *   Promise<version>. Revalidate edited text server-side; resolve only after a
 *   persisted commit. `version` is opaque and passed through unchanged.
 * - listScripts(): Promise<Array<{ id: string, name: string }>>.
 * - onCommitted(version): synchronous notification; should not throw.
 * - maxBytes?: positive byte limit, defaults to 1 MiB. Bind to server limits.
 *
 * Observed backend is snake_case/nested: adapt import_id + preview.text;
 * warning code -> id with a human-readable message (include source_line);
 * commit text -> edited_text, acknowledgedWarnings -> acknowledged_warnings.
 * Parent supplies title/script_id if required and maps list entries to id/name.
 * This module defines an injection interface, NOT wire endpoints or contracts.
 *
 * File.text() reads only size-checked .txt files; NUL/replacement characters and
 * oversized UTF-8 re-encoding are rejected conservatively. This is usability,
 * not a security boundary: server encoding/parsing/limits remain authoritative.
 * No automatic stage removal, exclusion inference, playback, clock, or fetch.
 * Requests are invalidated, not aborted: a pending commit can still persist
 * after navigating away; adapters must handle idempotency and reconcile state.
 */
export default function ScriptImport(props) {
	return <ImportPanel key={props.channelId} {...props} />;
}

function ImportPanel({ createImport, commitImport, listScripts, onCommitted, maxBytes = 1024 * 1024 }) {
	const [preview, setPreview] = useState(null);
	const request = useRef(0);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			request.current += 1;
		};
	}, []);
	const listRequest = useRef(0);
	const [scripts, setScripts] = useState(null);
	const [listError, setListError] = useState('');
	const [listBusy, setListBusy] = useState(false);
	const refresh = useCallback(async () => {
		const ticket = ++listRequest.current;
		setListBusy(true);
		setListError('');
		setScripts(null);
		try {
			const result = await listScripts();
			if (!mounted.current || ticket !== listRequest.current) return;
			if (!Array.isArray(result) || !result.every((item) => item && typeof item.id === 'string' && typeof item.name === 'string'))
				throw new Error('Invalid scripts response.');
			setScripts(result);
		} catch (failure) {
			if (mounted.current && ticket === listRequest.current) setListError((failure && failure.message) || 'Could not list scripts.');
		} finally {
			if (mounted.current && ticket === listRequest.current) setListBusy(false);
		}
	}, [listScripts]);
	useEffect(() => {
		refresh();
		return () => {
			listRequest.current += 1;
		};
	}, [refresh]);
	const [busy, setBusy] = useState('');
	const [error, setError] = useState('');
	const [text, setText] = useState('');
	const [acknowledged, setAcknowledged] = useState(false);
	const committing = useRef(false);
	const needsReview = preview && (preview.warnings.length > 0 || preview.exclusions.length > 0);
	async function select(event) {
		if (committing.current) return;
		const ticket = ++request.current;
		const file = event.target.files[0];
		event.target.value = '';
		setPreview(null);
		setText('');
		setAcknowledged(false);
		setError('');
		setBusy('');
		if (!file) return;
		setBusy('Reading and previewing…');
		try {
			if (!/\.(txt|vtt|srt)$/i.test(file.name) || (file.type && !SCRIPT_MIME_TYPES.includes(file.type))) throw new Error('Choose a .txt, .vtt or .srt file.');
			if (file.size > maxBytes) throw new Error('File exceeds the byte limit.');
			const source = await file.text();
			if (ticket !== request.current) return;
			if (!source.trim() || /[\u0000\uFFFD]/.test(source))
				throw new Error('Choose nonempty UTF-8 plain text without replacement characters or NUL bytes.');
			if (new Blob([source]).size > maxBytes) throw new Error('Text exceeds the UTF-8 byte limit.');
			const result = await createImport({ filename: file.name, text: source });
			if (ticket !== request.current) return;
			if (!validPreview(result)) throw new Error('Invalid preview response.');
			setPreview(result);
			setText(result.text);
		} catch (failure) {
			if (ticket === request.current) setError((failure && failure.message) || 'Could not read or import script.');
		} finally {
			if (ticket === request.current) setBusy('');
		}
	}
	async function commit() {
		if (committing.current || busy || !preview || !preview.valid || !validText(text, maxBytes) || (needsReview && !acknowledged)) return;
		const ticket = request.current;
		committing.current = true;
		setBusy('Committing…');
		setError('');
		try {
			const version = await commitImport({
				importId: preview.importId,
				text,
				acknowledgedWarnings: acknowledged ? preview.warnings.map((warning) => warning.id) : [],
			});
			if (!mounted.current || ticket !== request.current) return;
			setPreview(null);
			setText('');
			refresh();
			onCommitted(version);
		} catch (failure) {
			if (mounted.current && ticket === request.current) setError((failure && failure.message) || 'Could not commit script.');
		} finally {
			committing.current = false;
			if (mounted.current && ticket === request.current) setBusy('');
		}
	}
	return (
		<Stack spacing={2}>
			<Typography variant="h6">Import script</Typography>
			<Typography variant="body2">
				Choose a UTF-8 plain .txt script, or a .vtt or .srt subtitle file whose cue times become the script's reference timecodes (up to {maxBytes} bytes). Server parsing and validation are authoritative. Stage directions are not removed
				automatically; review and edit the spoken text. Timecodes are reference only; importing does not start playback.
			</Typography>
			<Typography>Saved scripts</Typography>
			{listBusy && (
				<Typography role="status" aria-label="Saved scripts loading">
					Loading saved scripts…
				</Typography>
			)}
			{listError && (
				<Typography role="alert" color="error">
					{listError}
				</Typography>
			)}
			{scripts &&
				(scripts.length ? (
					<ul>
						{scripts.map((item) => (
							<li key={item.id}>{item.name}</li>
						))}
					</ul>
				) : (
					<Typography>No saved scripts.</Typography>
				))}
			<Button onClick={refresh} disabled={listBusy}>
				Refresh saved scripts
			</Button>
			{busy && <Typography role="status">{busy}</Typography>}
			{error && (
				<Typography role="alert" color="error">
					{error}
				</Typography>
			)}
			<label>
				Script file
				<input type="file" disabled={busy === 'Committing…'} accept=".txt,.vtt,.srt,text/plain,text/vtt,application/x-subrip" onChange={select} />
			</label>
			{preview && !preview.valid && (
				<Typography role="alert" color="error">
					Server preview is not valid for commit. Choose another file.
				</Typography>
			)}
			{preview && (
				<TextField
					label="Spoken preview"
					disabled={busy === 'Committing…'}
					multiline
					minRows={6}
					value={text}
					onChange={(event) => {
						if (!committing.current) {
							setText(event.target.value);
							setAcknowledged(false);
						}
					}}
				/>
			)}
			{preview && (
				<>
					<Typography>Warnings</Typography>
					<ul aria-label="Warnings">
						{groupWarnings(preview.warnings).map((group) => (
							<li key={group.key}>
								{group.count > 1 ? `${group.code || group.message} × ${group.count}` : group.message}
								{group.lines.length > 1 && (
									<details>
										<summary>Show lines</summary>
										{group.lines.slice(0, MAX_LINES_SHOWN).join(', ')}
										{group.lines.length > MAX_LINES_SHOWN ? ` … and ${group.lines.length - MAX_LINES_SHOWN} more` : ''}
									</details>
								)}
							</li>
						))}
					</ul>
					<Typography>Excluded content</Typography>
					<ul>
						{preview.exclusions.map((item, index) => (
							<li key={index}>
								Line {item.line}: {item.text} — {item.reason}
							</li>
						))}
					</ul>
					{needsReview && (
						<Typography variant="body2">
							To review: {preview.warnings.length} {preview.warnings.length === 1 ? 'warning' : 'warnings'} in {groupWarnings(preview.warnings).length}{' '}
							{groupWarnings(preview.warnings).length === 1 ? 'group' : 'groups'}, {preview.exclusions.length} excluded {preview.exclusions.length === 1 ? 'passage' : 'passages'}.
						</Typography>
					)}
					{needsReview && (
						<label>
							<input
								type="checkbox"
								disabled={busy === 'Committing…'}
								checked={acknowledged}
								onChange={(event) => setAcknowledged(event.target.checked)}
							/>
							I reviewed the warnings and exclusions
						</label>
					)}
				</>
			)}
			<Button disabled={Boolean(busy) || !preview || !preview.valid || !validText(text, maxBytes) || (needsReview && !acknowledged)} onClick={commit}>
				Commit script
			</Button>
		</Stack>
	);
}
